import { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Globe,
  Info,
  Loader2,
  MapPin,
  Sparkles,
  Users,
} from "lucide-react";
import { fetchWithAuth } from "@/lib/api/client";

// Mejora G (changelog cliente): vista del agente de contexto
// geográfico/demográfico. Solicitado por Chema en la reunión 2026-04-10.

type SourceKind = "pliego" | "inferencia";

interface SourcedFactor {
  factor: string;
  relevance: string;
  source: SourceKind | string;
  source_detail?: string;
}

interface GeographicContextResult {
  territory?: {
    scope?: string;
    level?: "MUNICIPAL" | "COMARCAL" | "PROVINCIAL" | "AUTONOMICO" | "NACIONAL";
    confidence_note?: string;
  };
  demographic_profile?: {
    summary?: string;
    key_factors?: SourcedFactor[];
  };
  social_context?: {
    summary?: string;
    key_factors?: SourcedFactor[];
  };
  local_challenges?: Array<{
    challenge: string;
    impact_on_service: string;
    severity: "ALTA" | "MEDIA" | "BAJA" | string;
  }>;
  proposal_recommendations?: Array<{
    id?: string;
    title: string;
    rationale: string;
    where_to_apply?: string;
    priority: "ALTA" | "MEDIA" | "BAJA" | string;
  }>;
  summary?: string;
  confidence_note?: string;
  confidence?: number;
  sources?: string[];
}

interface GeographicContextViewProps {
  licitacionId: string;
  initialResult?: GeographicContextResult | null;
}

const severityStyles: Record<string, { bg: string; color: string; label: string }> = {
  ALTA: { bg: "bg-red-100", color: "text-red-700", label: "Alta" },
  MEDIA: { bg: "bg-yellow-100", color: "text-yellow-700", label: "Media" },
  BAJA: { bg: "bg-blue-100", color: "text-blue-700", label: "Baja" },
};

const sourceStyles: Record<string, { bg: string; color: string; label: string; icon: typeof BookOpen }> = {
  pliego: {
    bg: "bg-emerald-50",
    color: "text-emerald-700",
    label: "Del pliego",
    icon: BookOpen,
  },
  inferencia: {
    bg: "bg-amber-50",
    color: "text-amber-700",
    label: "Inferencia",
    icon: Sparkles,
  },
};

function SourceBadge({ source }: { source: string }) {
  const conf = sourceStyles[source] ?? sourceStyles.inferencia;
  const Icon = conf.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${conf.bg} ${conf.color}`}
    >
      <Icon className="w-3 h-3" />
      {conf.label}
    </span>
  );
}

function FactorList({ items }: { items: SourcedFactor[] }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-gray-500 italic">Sin factores identificados.</p>;
  }
  return (
    <ul className="space-y-3">
      {items.map((f, idx) => (
        <li key={idx} className="border-l-4 border-gray-200 pl-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-800">{f.factor}</span>
            <SourceBadge source={String(f.source ?? "inferencia")} />
          </div>
          <p className="text-sm text-gray-600 mt-1">{f.relevance}</p>
          {f.source_detail && (
            <p className="text-xs text-gray-500 mt-1 italic">Referencia: {f.source_detail}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function GeographicContextView({
  licitacionId,
  initialResult,
}: GeographicContextViewProps) {
  const [territoryHint, setTerritoryHint] = useState("");
  const [focus, setFocus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<GeographicContextResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Solo aceptamos el resultado si tiene la forma de este agente — si
    // llega de otro agente del historial, evitamos pintar campos inexistentes.
    if (
      initialResult &&
      (initialResult.territory ||
        initialResult.demographic_profile ||
        initialResult.proposal_recommendations)
    ) {
      setResult(initialResult);
    }
  }, [initialResult]);

  const handleAnalyze = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchWithAuth(
        `/api/v1/agents/geographic_context/execute`,
        {
          method: "POST",
          body: JSON.stringify({
            licitacion_id: licitacionId,
            input_data: {
              ...(territoryHint.trim() && { territory_hint: territoryHint.trim() }),
              ...(focus.trim() && { focus: focus.trim() }),
            },
          }),
        }
      );
      setResult(data.result as GeographicContextResult);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error al analizar el contexto geográfico"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const confidencePct =
    result?.confidence !== undefined
      ? Math.round(result.confidence * 100)
      : null;

  return (
    <div className="space-y-6">
      {/* Descripción del agente */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex gap-3">
        <Globe className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-teal-800">
          <p className="font-semibold mb-1">Contexto Geográfico y Demográfico</p>
          <p>
            Analiza el ámbito territorial del pliego y el perfil de la población
            a la que se dirige el servicio. Propone mejoras concretas para
            anclar la propuesta al contexto local (perfil de usuarios, retos
            socioeconómicos, particularidades culturales).
          </p>
          <p className="mt-2 text-xs text-teal-700">
            Combina datos del pliego con conocimiento general del modelo. Cada
            afirmación se etiqueta como{" "}
            <strong>Del pliego</strong> o <strong>Inferencia</strong> para que
            puedas validar localmente lo segundo.
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Parámetros del análisis
        </h3>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="geographic-hint"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Ámbito territorial{" "}
              <span className="text-gray-400 font-normal">opcional</span>
            </label>
            <input
              id="geographic-hint"
              type="text"
              value={territoryHint}
              onChange={(e) => setTerritoryHint(e.target.value)}
              placeholder="Ej: Almería capital, Comarca del Poniente, Granada (Genil)"
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Útil si el pliego no permite inferir bien la ubicación o quieres
              centrar el análisis en una zona concreta.
            </p>
          </div>

          <div>
            <label
              htmlFor="geographic-focus"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Foco específico{" "}
              <span className="text-gray-400 font-normal">opcional</span>
            </label>
            <input
              id="geographic-focus"
              type="text"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="Ej: perfil de los menores, contexto laboral local, comunidad migrante"
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          <button
            type="button"
            onClick={handleAnalyze}
            disabled={isLoading}
            className="w-full bg-teal-600 text-white font-medium py-2 px-4 rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analizando contexto local...
              </>
            ) : (
              <>
                <Globe className="w-5 h-5 mr-2" />
                Analizar contexto local
              </>
            )}
          </button>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>

      {/* Resultados */}
      {result && (
        <div className="space-y-6">
          {/* Resumen + ámbito */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Ámbito territorial
                  </h3>
                  {result.territory?.scope && (
                    <p className="text-sm text-gray-700 mt-1">
                      <span className="font-medium">{result.territory.scope}</span>
                      {result.territory.level && (
                        <span className="text-gray-500"> · {result.territory.level}</span>
                      )}
                    </p>
                  )}
                  {result.territory?.confidence_note && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {result.territory.confidence_note}
                    </p>
                  )}
                </div>
              </div>
              {confidencePct !== null && (
                <div className="text-right">
                  <div className="text-xs text-gray-500">Confianza global</div>
                  <div
                    className={`text-2xl font-bold ${
                      confidencePct >= 70
                        ? "text-emerald-600"
                        : confidencePct >= 50
                        ? "text-amber-600"
                        : "text-orange-600"
                    }`}
                  >
                    {confidencePct}%
                  </div>
                </div>
              )}
            </div>

            {result.summary && (
              <div className="bg-gray-50 rounded-md p-4">
                <p className="text-sm text-gray-700">{result.summary}</p>
              </div>
            )}
            {result.confidence_note && (
              <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{result.confidence_note}</span>
              </div>
            )}
          </div>

          {/* Perfil demográfico */}
          {result.demographic_profile && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Perfil demográfico
                </h3>
              </div>
              {result.demographic_profile.summary && (
                <p className="text-sm text-gray-700 mb-4">
                  {result.demographic_profile.summary}
                </p>
              )}
              <FactorList items={result.demographic_profile.key_factors ?? []} />
            </div>
          )}

          {/* Contexto social */}
          {result.social_context && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-5 h-5 text-teal-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Contexto social
                </h3>
              </div>
              {result.social_context.summary && (
                <p className="text-sm text-gray-700 mb-4">
                  {result.social_context.summary}
                </p>
              )}
              <FactorList items={result.social_context.key_factors ?? []} />
            </div>
          )}

          {/* Retos locales */}
          {result.local_challenges && result.local_challenges.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Retos locales identificados
                </h3>
              </div>
              <ul className="space-y-3">
                {result.local_challenges.map((c, idx) => {
                  const sev = severityStyles[c.severity] ?? severityStyles.MEDIA;
                  return (
                    <li
                      key={idx}
                      className="border border-gray-200 rounded-md p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-800">
                          {c.challenge}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${sev.bg} ${sev.color}`}
                        >
                          {sev.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {c.impact_on_service}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Recomendaciones para la propuesta */}
          {result.proposal_recommendations &&
            result.proposal_recommendations.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Recomendaciones para la propuesta
                  </h3>
                </div>
                <ul className="space-y-3">
                  {result.proposal_recommendations.map((r, idx) => {
                    const prio = severityStyles[r.priority] ?? severityStyles.MEDIA;
                    return (
                      <li
                        key={r.id ?? idx}
                        className="border-l-4 border-emerald-200 pl-4 py-2"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-800">
                            {r.title}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${prio.bg} ${prio.color}`}
                          >
                            Prioridad {prio.label}
                          </span>
                          {r.where_to_apply && (
                            <span className="text-[11px] text-gray-500">
                              → {r.where_to_apply}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{r.rationale}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
